/**
 * The dashboard's completion numbers: pure helpers (see ./view.ts).
 *
 * Data: omon-api /v1/chase_completion, totalling the hourly
 * `_ops/chase_completion` summary (app.dsr dsr/completion.py).
 */

export type Counts = Partial<Record<
  "on-time" | "late" | "form" | "owed" | "overdue" | "not-owed"
  | "double-entered" | "not-entered" | "no-plan", number>>;

export interface PracticeRow {
  name: string;
  shifts: number;
  counts: Counts;
  from?: string | null;
  machformUntil?: string | null;
}

export interface ClientRow {
  eocId: string | null;
  name: string | null;
  practice: string;
  shifts: number;
  counts: Counts;
}

export interface CompletionResponse {
  available: boolean;
  generatedAt?: string;
  days?: number;
  defaultPractice?: string | null;
  practices?: PracticeRow[];
  clients?: ClientRow[];
}

export interface Tally {
  /** Shifts that owed a record: from the practice's go-live date, on a plan. */
  shifts: number;
  /** Shifts before go-live, on a plan. Never mixed into `shifts`: a practice
   *  going live part way through a period would otherwise look far behind. */
  preLiveShifts: number;
  completed: number;
  onTime: number;
  late: number;
  /** Cleared by a MachForm during a practice's grace period. */
  byForm: number;
  stillOwed: number;
  overdue: number;
  /** Shifts on a client with no plan, so the app could not take a record. */
  noPlan: number;
  /** Before go-live: a carer double entered, which nobody is asked to do yet. */
  doubleEntered: number;
  notEntered: number;
}

/** "Not owed" closures are out of both sides: nobody owed that record. */
export function tally(c: Counts): Tally {
  const n = (k: keyof Counts) => c[k] ?? 0;
  const completed = n("on-time") + n("late");
  const stillOwed = n("owed") + n("overdue");
  return {
    shifts: completed + n("form") + stillOwed,
    preLiveShifts: n("double-entered") + n("not-entered"),
    completed,
    onTime: n("on-time"),
    late: n("late"),
    byForm: n("form"),
    stillOwed,
    overdue: n("overdue"),
    noPlan: n("no-plan"),
    doubleEntered: n("double-entered"),
    notEntered: n("not-entered"),
  };
}

export function isLive(p: Pick<PracticeRow, "from">): boolean {
  return !!p.from;
}

export function pct(n: number, of: number): number {
  return of > 0 ? Math.round((n / of) * 100) : 0;
}

/** Never pretend a percentage out of nothing. */
export function pctLabel(n: number, of: number): string {
  return of > 0 ? `${pct(n, of)}%` : "–";
}

/** The saved choice if that practice still has shifts, else the person's own
 *  practice, else every practice. A stale or unknown name must not blank the page. */
export function startingPractice(
  saved: string | null,
  own: string | null | undefined,
  practices: Pick<PracticeRow, "name">[],
): string {
  const known = new Set(practices.map((p) => p.name));
  if (saved === ALL) return ALL;
  if (saved && known.has(saved)) return saved;
  if (own && known.has(own)) return own;
  return ALL;
}

export const ALL = "__all__";
